/**
 * TipTapEditor — Rich text editor component
 * Drop-in replacement for ReactQuill in EditFormV1
 *
 * Props:
 *   value      string    — HTML string (controlled)
 *   onChange   function  — called with HTML string on every change
 *   disabled   boolean   — makes editor read-only
 *   placeholder string   — placeholder text
 */

import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import '../styles/TipTapEditor.css';

// ─── Toolbar button ───────────────────────────────────────────────────────────

function ToolbarButton({ onClick, active, disabled, title, children }) {
    return (
        <button
            type="button"
            title={title}
            disabled={disabled}
            className={`tte-btn${active ? ' tte-btn--active' : ''}`}
            onMouseDown={(e) => {
                e.preventDefault(); // prevent editor losing focus
                onClick?.();
            }}
        >
            {children}
        </button>
    );
}

// ─── Toolbar ─────────────────────────────────────────────────────────────────

function Toolbar({ editor, disabled }) {
    if (!editor) return null;

    const setLink = () => {
        const url = window.prompt('Enter URL');
        if (!url) {
            editor.chain().focus().unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({ href: url }).run();
    };

    return (
        <div className={`tte-toolbar${disabled ? ' tte-toolbar--disabled' : ''}`}>
            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Bold"
                    disabled={disabled}
                    active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <strong>B</strong>
                </ToolbarButton>

                <ToolbarButton
                    title="Italic"
                    disabled={disabled}
                    active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <em>I</em>
                </ToolbarButton>

                <ToolbarButton
                    title="Underline"
                    disabled={disabled}
                    active={editor.isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                >
                    <span style={{ textDecoration: 'underline' }}>U</span>
                </ToolbarButton>

                <ToolbarButton
                    title="Strikethrough"
                    disabled={disabled}
                    active={editor.isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                    <s>S</s>
                </ToolbarButton>
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Bullet List"
                    disabled={disabled}
                    active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    ≡
                </ToolbarButton>

                <ToolbarButton
                    title="Ordered List"
                    disabled={disabled}
                    active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    1.
                </ToolbarButton>
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Link"
                    disabled={disabled}
                    active={editor.isActive('link')}
                    onClick={setLink}
                >
                    🔗
                </ToolbarButton>
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Clear formatting"
                    disabled={disabled}
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                >
                    ✕
                </ToolbarButton>
            </div>
        </div>
    );
}

// ─── Main editor ──────────────────────────────────────────────────────────────

export default function TipTapEditor({
    value = '',
    onChange,
    disabled = false,
    placeholder = '',
}) {
    // Tracks the last value this editor itself produced (via onUpdate) or was
    // last told to show (via the sync effect below) — NOT the editor's live
    // getHTML(), which can drift from `value` after a round-trip through
    // TipTap's HTML serializer (e.g. plain AI-generated text with no <p> tags
    // never equals its own wrapped-in-<p> serialization, which previously made
    // the old getHTML()-based comparison useless as a "did this come from us"
    // check and caused an external update to fight with a stale echo).
    const lastKnownValueRef = useRef(value ?? '');

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { rel: 'noopener noreferrer' },
            }),
        ],
        content: value,           // set initial content correctly on mount
        editable: !disabled,
        editorProps: {
            attributes: {
                class: 'tte-content',
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const next = html === '<p></p>' ? '' : html;
            lastKnownValueRef.current = next;
            onChange?.(next);
        },
    });

    // Sync value from outside — handles setFieldsValue from Ant Design form
    // (e.g. an AI Action replacing the content). Skips only when the incoming
    // value is exactly what this editor itself last emitted, so a genuine
    // external update always applies even if it differs from getHTML() purely
    // due to HTML serialization (missing <p> wrapper, entity encoding, etc).
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const nextValue = value || '';
        if (nextValue === (lastKnownValueRef.current || '')) return;
        lastKnownValueRef.current = nextValue;
        editor.commands.setContent(nextValue, false);
    }, [value, editor]);

    // Sync disabled state
    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [disabled, editor]);

    return (
        <div className={`tte-wrapper${disabled ? ' tte-wrapper--disabled' : ''}`}>
            <Toolbar editor={editor} disabled={disabled} />
            <EditorContent editor={editor} />
            {!value && !editor?.isFocused && placeholder && (
                <div className="tte-placeholder">{placeholder}</div>
            )}
        </div>
    );
}
